import React from "react";
import internalStateService from "../app/InternalStateService";
import { getDataTqId } from "./testIdUtils";
import ValidationService from "../app/ValidationService";

import { resolveStaticBinding, resolveItemRegexpBinding } from "./bindingUtils";
import { resolve } from "./../factories/ComponentsFactory";
import deepEquals from "./deepEquals";
//     props - this parameter contains the data parameters: data, config and uiState.
//     options:
//     options.validatable - boolean. if true the method add to result the necessary parameters: validationMethod, failedValidations and, valid.
//     options.componentProps - array of component props.
//     options.additionalProps - array of component props.
//     options.aliases - some props may have two posible names. Via aliases one can define another name for a given property.
//     options.propsDefaults - default values
//     options.skipDataTqId - don't add dataTqId
export default {
    extractComponentProps: (props, options = {}) => {
        let configProps = props.config || {};
        let data = props.data && props.data.properties ? props.data.properties : {};
        let propsDefaults = options.propsDefaults || {};
        let config = Object.assign(
            {},
            propsDefaults,
            internalStateService.getComponentsDefaultConfig({ widgetId: props.widgetId })[
                configProps.type
            ],
            configProps
        );
        let resultProps = {};
        let componentProps = options.componentProps || {};
        let commonAliases = {
            required: "__required",
            hint: "__hint"
        };
        let aliases = Object.assign(commonAliases, options.aliases || {});
        let extraProps = options.additionalProps || [];
        let propsToExtract = Array.from(new Set(Object.keys(componentProps).concat(extraProps)));

        for (let prop of propsToExtract) {
            if (getPropValue(prop, data, aliases) !== undefined) {
                resultProps[prop] = getPropValue(prop, data, aliases);
                //search in config
            } else if (getPropValue(prop, config, aliases) !== undefined) {
                resultProps[prop] = getPropValue(prop, config, aliases);
            }
        }

        if (config.hasOwnProperty("enabled")) {
            resultProps.disabled = !config.enabled;
        }

        //editable
        if (data.hasOwnProperty("enabled")) {
            resultProps.disabled = !data.enabled;
        }

        //dataTqId
        if (!options.skipDataTqId) {
            resultProps.dataTqId = configProps.dataTqId || getDataTqId(props);
        }

        //validatable
        if (options.validatable) {
            let validations = props.data && props.data.validations ? props.data.validations : {};
            resultProps.validationMethod = ValidationService.getValidationMethod(data, validations);
            if (props.uiState) {
                if (props.uiState.hasOwnProperty("valid")) {
                    resultProps["valid"] = props.uiState["valid"];
                }
                if (props.uiState.hasOwnProperty("failedValidations")) {
                    resultProps["failedValidations"] = props.uiState["failedValidations"];
                }
            }
        }

        return resultProps;
    }
};
export const setDataInStore = (
    { config = {}, data = {}, truePath, widgetId, triggerSet } = {},
    toSet
) => {
    //TODO we need to update triggerSet to work with computedBindTo instead.
    let pathInStore = truePath || config.bindTo;
    let dataProps = data.properties || {};
    let storeNeedUpdate = !deepEquals(toSet, dataProps);
    if (storeNeedUpdate) {
        triggerSet([widgetId, ...pathInStore], toSet);
    }
    return storeNeedUpdate;
};
export const getEmptyContent = (emptyText = "", dataName = "") => (
    <span data-no-content="true" data-constructor-name={dataName}>
        {emptyText}
    </span>
);
export const resolveBinding = ({
    config = {},
    computedBindTo,
    parentBindTo,
    itemClassifier,
    index
} = {}) => {
    if (computedBindTo) {
        return computedBindTo;
    } else {
        let bindTo = config.bindTo;
        return resolveStaticBinding(bindTo, parentBindTo, index, itemClassifier);
    }
};

export const resolveChild = (
    { config = {}, computedBindTo, parentBindTo, itemClassifier, index, widgetId } = {},
    childConfig,
    childIndex,
    options = {}
) => {
    const props = {
        config,
        computedBindTo,
        parentBindTo,
        itemClassifier,
        index
    };
    let resolvedParentBindTo = resolveBinding(props);

    return resolve(
        childConfig,
        Object.assign(
            {
                key: childIndex,
                index: childIndex,
                widgetId: widgetId,
                parentBindTo: resolvedParentBindTo
            },
            options
        )
    );
};
export const resolveChildren = (
    { config = {}, data = {}, computedBindTo, parentBindTo, itemClassifier, index, widgetId } = {},
    options = {}
) => {
    let children = config.children;
    let items = config.items;
    let itemConfig = config.item;

    const props = { config, data, computedBindTo, parentBindTo, itemClassifier, index, widgetId };

    if (children) {
        //standard resolving
        return children.map((child, idx) => {
            return resolveChild(props, child, idx, options);
        });
    }

    if (items) {
        let createdChildren = [];
        let dataEntities = data.entities || [];

        // For each data entity, find a matching configuration object (within the "items" array),
        // and create new component with it
        // if no configuration is found the item is not rendered
        createdChildren = [];
        dataEntities.forEach((entity, dataIndex) => {
            for (let j = 0; j < items.length; j++) {
                let item = items[j];
                // Resolve regular expression matching. If a match is found edit the bindTo property of the
                // copied configuration object.
                let resolvedItem = resolveItemRegexpBinding(item, entity.class[0]);

                if (resolvedItem) {
                    createdChildren.push(
                        resolveChild(props, resolvedItem.item, dataIndex, options)
                    );
                }
            }
        });

        return createdChildren;
    }

    //collection resolving
    if (itemConfig && data) {
        return resolveCollectionItems(props, options);
    }

    return [];
};
export const resolveRegions = (
    { config = {}, computedBindTo, parentBindTo, itemClassifier, index, widgetId } = {},
    options = {}
) => {
    let regions = { default: [] };
    let children = config.children || config.components;
    let childConfig, child;
    const props = { config, computedBindTo, parentBindTo, itemClassifier, index, widgetId };
    for (let i = 0; i < children.length; i++) {
        let childRegion;
        childConfig = children[i];
        if (childConfig.region && typeof childConfig.region === "string") {
            childRegion = childConfig.region;
        } else if (childConfig.region && childConfig.region.id) {
            childRegion = childConfig.region.id;
        }
        child = resolveChild(props, childConfig, i, options);
        if (childRegion) {
            if (!regions[childRegion]) {
                regions[childRegion] = [child];
            } else {
                regions[childRegion].push(child);
            }
        } else {
            regions["default"].push(child);
        }
    }
    return regions;
};
export const resolveCollectionItems = (
    { config = {}, data = {}, computedBindTo, parentBindTo, itemClassifier, index, widgetId } = {},
    options = {}
) => {
    let collectionItemsConfig = options.config || config;
    let collectionItems = options.items || data.entities || [];
    const resolveBindingParams = {
        config: collectionItemsConfig,
        computedBindTo,
        parentBindTo,
        itemClassifier,
        index
    };
    let collectionItemsComputedBindTo =
        options.computedBindTo || resolveBinding(resolveBindingParams);
    let collectionItemsWidgetId = options.widget || widgetId;
    let itemConfig = options.itemConfig || collectionItemsConfig["item"];

    return collectionItems.map((item, idx) => {
        let itemProps = item.properties || {};
        //using unique ids if possible
        //to avoid issue on collection re-render with different data
        //props.key used by ReactJS
        //to decide whether to render the same component or create a new one
        //currently siren contract is not supporting properties in collection item
        //the collection items indexes are described in the classifiers always on first place
        let itemId;
        let itemClassifier;
        if (Array.isArray(item.class) && item.class.length) {
            itemClassifier = item.class[0];
        }
        itemId = itemProps.id || itemClassifier || idx;
        return resolve(itemConfig, {
            index: idx,
            key: itemId,
            widgetId: collectionItemsWidgetId,
            itemClassifier: itemClassifier,
            parentBindTo: collectionItemsComputedBindTo
        });
    });
};
export const dispatchSirenActions = (
    {
        config = {},
        data = {},
        computedBindTo,
        widgetId,
        dispatchSirenActions: propsDispatchSirenActions
    } = {},
    targetEvent,
    childDispatching = {},
    options = {}
) => {
    let actions = data.actions || [];
    let configActions = config.actions;

    //dispatchSirenAction's consumer can specify which actions to be triggered
    if (options.action) {
        actions = options.action;
    } else {
        //By default web-ui will check of the child defines actions and either dispatch them
        //or dispatch the action on parent level. This is done to allow children to override
        //parent actions. In case we have a lot of items which should dispatch the same action
        //and few items that should dispatch different actions - we define the common action
        //on parent level, then we override the few item cases with special actions.
        //This also applies for configuration actions.
        if (childDispatching) {
            actions = childDispatching.actions || actions;
            computedBindTo = childDispatching.computedBindTo || computedBindTo;
            configActions = childDispatching.configActions || configActions;
        }
        if (Array.isArray(configActions)) {
            actions = configActions.concat(actions);
        }
    }

    if (!actions.length) {
        return;
    }

    if (propsDispatchSirenActions) {
        return propsDispatchSirenActions(actions, widgetId, computedBindTo, targetEvent, {
            returnPromise: options.returnPromise
        });
    } else {
        const dispatchSirenActionsMethod = internalStateService.getActionByName(
            "siren.dispatchSirenActions"
        );
        const dispatch = internalStateService.getDispatchMethod();
        return dispatch(
            dispatchSirenActionsMethod(actions, widgetId, computedBindTo, targetEvent, {
                returnPromise: options.returnPromise
            })
        );
    }
};
export const saveState = (
    { computedBindTo, truePath, widgetId, saveState: propsSaveState } = {},
    newValue,
    state = {},
    extraState = {}
) => {
    let newState = Object.assign({}, extraState, {
        value: newValue
    });

    let uiState = {
        failedValidations: state.failedValidations,
        valid: state.valid
    };

    if (propsSaveState) {
        propsSaveState(widgetId, computedBindTo, truePath, newState, uiState);
    } else {
        const saveStateAction = internalStateService.getActionByName("component.saveState");
        const dispatch = internalStateService.getDispatchMethod();
        dispatch(saveStateAction(widgetId, computedBindTo, truePath, newState, uiState));
    }
};
export const buildEnumData = ({ data = {} } = {}) => {
    let itemsData = data.entities || [];

    if (itemsData.length) {
        if (itemsData[0].class && itemsData[0].class.includes("enum")) {
            itemsData = itemsData[0].entities || [];
        }
    }

    return itemsData;
};

//TODO to be deprecated after check usage in projects
export const isEqualArrays = (firstArray = [], secondArray = []) => {
    let equalArrays = true;

    if (firstArray.length !== secondArray.length) {
        return false;
    }

    for (let i = 0; i < firstArray.length; i++) {
        if (!secondArray.includes(firstArray[i])) {
            equalArrays = false;
        }
    }

    return equalArrays;
};

const getPropValue = (dataPropName, data, aliases) => {
    if (aliases[dataPropName] && data[aliases[dataPropName]] !== undefined) {
        return data[aliases[dataPropName]];
    } else {
        return data[dataPropName];
    }
};
